<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-md bg-gray-800 text-gray-300 rounded-lg p-2 sm:p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Header -->
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-semibold text-gray-100 flex items-center gap-2">
          <i class="fas fa-ghost text-indigo-400 text-xs"></i>
          ไล่ผี
        </h3>
        <button @click="$emit('close')" class="text-gray-300 hover:text-indigo-300 group">
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>

      <!-- Main Content -->
      <div class="overflow-y-auto scrollbar-thin" id="style-1">
        <!-- Loading -->
        <div v-if="isLoading" class="flex flex-col items-center py-8">
          <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
          <p class="text-gray-300 text-sm mt-4">กำลังโหลดข้อมูล...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="flex flex-col gap-4">
          <div class="text-center text-red-300 py-6 text-sm bg-gray-800 border border-gray-600 rounded-md">
            {{ error }}
          </div>
          <button
            @click="$emit('close')"
            class="bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 font-semibold border border-gray-600 flex items-center justify-center gap-2"
          >
            <i class="fas fa-times text-xs"></i> ปิด
          </button>
        </div>

        <!-- Result -->
        <div v-else-if="showResult" class="flex flex-col gap-4">
          <div class="p-2 bg-gray-800 border rounded-md transition-all duration-200 border-l-4 border-blue-400">
            <div class="flex items-center gap-2 text-blue-400 text-xs sm:text-sm">
              <i class="fas fa-check"></i>
              <span class="text-gray-300">ใช้ {{ item.name || 'ไอเทม' }} เพื่อไล่ผีเป้าหมายสำเร็จ</span>
            </div>
          </div>
          <button
            @click="$emit('close')"
            class="bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 font-semibold border border-gray-600 flex items-center justify-center gap-2 w-full"
          >
            <i class="fas fa-times text-xs"></i> ปิด
          </button>
        </div>

        <!-- Content -->
        <div v-else class="flex flex-col gap-4">
          <!-- Opponent (Target) -->
          <div v-if="hasTarget" class="bg-gray-800 rounded-md p-1.5 flex items-center border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900 cursor-pointer gap-2">
            <img
              :src="displayImage"
              alt="Opponent"
              class="w-9 h-9 rounded-md object-cover border-2 border-gray-600"
            />
            <div class="flex flex-col">
              <h4 class="text-gray-300 text-xs sm:text-sm font-semibold">{{ displayName }}</h4>
            </div>
          </div>
          <div v-else class="text-yellow-400 text-center text-xs sm:text-sm">
            <i class="fas fa-exclamation-triangle mr-2 text-yellow-400"></i> ต้องค้นหาผู้รอดชีวิตในเมนูก่อนใช้งาน
          </div>

          <!-- Item -->
          <div class="bg-gray-800 rounded-md p-1.5 flex items-center border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900 cursor-pointer gap-2">
            <img
              :src="item.img || defaultItemImg"
              alt="Item"
              class="w-9 h-9 rounded-md object-cover border-2 border-gray-600"
              @error="handleImageError"
            />
            <div class="flex flex-col">
              <h4 class="text-gray-300 text-xs sm:text-sm font-semibold truncate">{{ item.name || 'Unknown' }}</h4>
            </div>
          </div>

          <!-- Warning Message -->
          <div class="text-yellow-400 text-xs sm:text-sm text-center">
            <i class="fas fa-exclamation-triangle mr-2 text-yellow-400"></i>
            เมื่อใช้งานแล้ว วิญญาณที่เฝ้าห้องและวิญญาณผู้พิทักษ์ของเป้าหมายจะถูกกำจัดทันที
          </div>

          <!-- Actions -->
          <div class="flex flex-col gap-2">
            <button
              @click="startExorcise"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 border border-indigo-700"
              :disabled="isExorcising || !hasTarget"
            >
              <span v-if="!isExorcising"><i class="fas fa-ghost text-xs"></i> ยืนยันไล่ผี</span>
              <span v-else class="flex items-center gap-2">
                <div class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
                กำลังไล่ผี...
              </span>
            </button>

            <button
              @click="$emit('close')"
              class="bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 border border-gray-600"
            >
              <i class="fas fa-times text-xs"></i> ยกเลิก
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import gameItems from '@/data/gameItems.json';
import defaultItemImg from '@/assets/default-item.png';

export default {
  name: 'ExorciseModal',
  props: {
    itemId: { type: String, required: true },
    item: { type: Object, default: null }
  },
  emits: ['close', 'exorcise-success'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const isLoading = ref(true);
    const error = ref(null);
    const isExorcising = ref(false);
    const hasTarget = ref(false);
    const opponent = ref({});
    const characterPublic = ref({});
    const item = ref({});
    const showResult = ref(false);

    const anonymousImage = 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2Fanonymouse.png?alt=media&token=25259b2d-03f1-49fb-b70c-054cd88d2a86';

    const displayName = computed(() => {
      if (!opponent.value || characterPublic.value?.building === 1 || characterPublic.value?.building === 2) return 'บุคคลลึกลับ';
      return opponent.value.characterName || 'บุคคลลึกลับ';
    });

    const displayImage = computed(() => {
      if (!opponent.value || characterPublic.value?.building === 1 || characterPublic.value?.building === 2) return anonymousImage;
      return opponent.value.imageUrl || anonymousImage;
    });

    const handleImageError = (e) => { e.target.src = defaultItemImg; };

    const instanceId = computed(() => props.item?.instanceId || props.item?.id || props.itemId);

    const inventoryStore = useInventoryStore();

    const hydrateItem = async () => {
      if (props.item?.name) {
        item.value = props.item;
        return true;
      }

      const bagData = inventoryStore.bagData;
      const bagItem = bagData?.items?.[instanceId.value] || null;
      if (!bagItem) {
        error.value = 'ไม่พบข้อมูลไอเทม';
        return false;
      }

      const template = gameItems[bagItem.templateId] || {};
      const states = bagItem.states || {};
      item.value = {
        instanceId: instanceId.value,
        id: instanceId.value,
        templateId: bagItem.templateId,
        states,
        ...template,
        ...states,
        name: states.name || template.name || bagItem.templateId,
        type: states.type || template.type || '',
      };
      return true;
    };

    const fetchData = async () => {
      try {
        const hasItem = await hydrateItem();
        if (!hasItem) {
          return;
        }
        // Private
        const data = playerStore.characterPrivateData;
        if (data) {
          const assassinateSurvivorUid = data.assassinateSurvivorUid;
          if (assassinateSurvivorUid) {
            hasTarget.value = true;
            const opponentDoc = await projectFirestore.collection('characters').doc(assassinateSurvivorUid).get();
            if (opponentDoc.exists) {
              opponent.value = opponentDoc.data();
            } else {
              error.value = 'ไม่พบข้อมูลเป้าหมาย';
              hasTarget.value = false;
            }
          } else {
            hasTarget.value = false;
          }
        } else {
          error.value = 'ไม่พบข้อมูลส่วนตัว';
          hasTarget.value = false;
        }
        // Fetch character public data from store
        characterPublic.value = playerStore.characterData || {};
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message;
        hasTarget.value = false;
      } finally {
        isLoading.value = false;
      }
    };

    const startExorcise = async () => {
      if (!hasTarget.value) return;
      isExorcising.value = true;
      showResult.value = false;

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'exorciseGhosts', instanceId: instanceId.value });
        showResult.value = true;
        emit('exorcise-success', instanceId.value);
      } catch (err) {
        error.value = err?.message || 'เกิดข้อผิดพลาดในการไล่ผี';
      } finally {
        isExorcising.value = false;
      }
    };

    onMounted(() => {
      if (user.value?.uid) {
        playerStore.init(user.value.uid);
      }
      fetchData();
    });

    return {
      // state
      isLoading,
      error,
      isExorcising,
      hasTarget,
      opponent,
      characterPublic,
      item,
      // result
      showResult,
      // computed
      displayName,
      displayImage,
      defaultItemImg,
      // methods
      startExorcise,
      handleImageError
    };
  }
};
</script>

<style scoped>
/* Thin scrollbar (custom) */
.scrollbar-thin::-webkit-scrollbar { width: 6px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #111827; border-radius: 8px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #374151; border-radius: 8px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #4b5563; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #374151 #111827; }

/* Animations */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }

@keyframes scaleIn { from { transform: scale(0.98); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.25s ease-out; }
</style>
